// Automatic, silent screenshot capture for the Send Feedback flow
// (SuggestionPanel.jsx). Nothing about this is shown to the agent: no
// thumbnail, no checkbox, no caption in the dialog.
//
// 2026-10-20: the screenshot was often missing, dropped on the client with no
// trace (the server never received one). It captured the whole app (the full
// queue list, hidden contact tabs, every image fetched again), then retried at
// smaller scales one after another, all inside a 2.5 s race before the dialog
// opened, and a busy screen ran out of time. Now:
//   - only what is on screen: the visible viewport, at the root's scroll
//     offset, skipping nodes that aren't rendered (display: none, e.g. a
//     hidden contact tab) and every MUI dialog, popover or tooltip;
//   - the capture starts on the click and runs in the background (up to
//     FEEDBACK_SCREENSHOT_CAPTURE_TIMEOUT_MS) while the dialog opens at once;
//     Submit waits for it, up to that cap, then submits with or without it;
//   - PNG at scale 1; over FEEDBACK_SCREENSHOT_MAX_BYTES, JPEG (quality 0.8)
//     at scale 1, then 0.75;
//   - why one is missing is sent as screenshotStatus: 'ok' | 'timeout' |
//     'error' | 'too_large' | 'none', stored on the feedback row.
//
// 2026-10-29 ("As the CSR saw it"): the whole suggestion panel, steps scrolled
// out of view included. The picture is the viewport made taller by the
// panel's hidden height; in the clone the panel's column (PANEL_SELECTOR) and
// the boxes around it stop clipping, so the panel is drawn from its top to its
// end. The size cap and the JPEG fallback are unchanged.
import { domToBlob } from 'modern-screenshot'

export const FEEDBACK_SCREENSHOT_MAX_BYTES = 1.5 * 1024 * 1024 // 1.5 MB, the server's cap too
export const FEEDBACK_SCREENSHOT_CAPTURE_TIMEOUT_MS = 15_000
export const SCREENSHOT_STATUSES = Object.freeze(['ok', 'timeout', 'error', 'too_large', 'none'])

// PNG first (flat panels and text compress well), then JPEG.
export const CAPTURE_ATTEMPTS = Object.freeze([
  Object.freeze({ type: 'image/png', scale: 1 }),
  Object.freeze({ type: 'image/jpeg', quality: 0.8, scale: 1 }),
  Object.freeze({ type: 'image/jpeg', quality: 0.8, scale: 0.75 }),
])

// A 1x1 transparent PNG data URL: modern-screenshot uses it for any image
// that fails to load (a cross-origin image with no CORS headers) instead of
// failing the whole capture.
const TRANSPARENT_PLACEHOLDER = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII='

// Overlays never belong in the picture (the feedback dialog itself included).
const OVERLAY_SELECTOR = '.MuiDialog-root, .MuiPopover-root, .MuiPopper-root, .MuiTooltip-popper, .MuiModal-root, [role="dialog"], [role="tooltip"]'

// The suggestion panel's scrolling column (App.jsx).
export const PANEL_SELECTOR = '[data-testid="suggestion-panel-column"]'

// How much of the panel is hidden by its own scroll: the height the picture
// grows by. 0 without a panel.
export function panelHiddenHeight(panel) {
  if (!panel) return 0
  const extra = Number(panel.scrollHeight ?? 0) - Number(panel.clientHeight ?? 0)
  return Number.isFinite(extra) && extra > 0 ? Math.ceil(extra) : 0
}

// In the clone: the panel's column grows to its content and nothing between
// it and the captured root clips it, so the steps below the fold are drawn.
// The clone has no scroll position, so the panel starts at its top.
export function unclipPanelInClone(clonedRoot, selector = PANEL_SELECTOR) {
  const panel = clonedRoot?.querySelector?.(selector)
  if (!panel?.style) return false
  panel.style.overflow = 'visible'
  panel.style.height = 'auto'
  panel.style.maxHeight = 'none'
  for (let el = panel.parentElement; el && el !== clonedRoot.parentElement; el = el.parentElement) {
    if (el.style) el.style.overflow = 'visible'
    if (el === clonedRoot) break
  }
  return true
}

// Whether a node is captured: not an overlay, and rendered (display: none
// nodes, such as a hidden contact tab, are skipped with all they contain).
// win: injectable for tests.
export function shouldCaptureNode(node, win = globalThis.window) {
  if (!node || node.nodeType !== 1) return true
  if (typeof node.matches === 'function' && node.matches(OVERLAY_SELECTOR)) return false
  const style = win?.getComputedStyle ? win.getComputedStyle(node) : null
  if (style && style.display === 'none') return false
  return true
}

// modern-screenshot options for one attempt: the visible viewport (the
// window's inner size) at the root's current scroll offset, with the node
// filter; with a panel, taller by the panel's hidden height and the panel
// unclipped in the clone.
export function captureOptions(root, attempt, win = globalThis.window, panel = null) {
  const width = win?.innerWidth ?? root?.clientWidth ?? 0
  const height = (win?.innerHeight ?? root?.clientHeight ?? 0) + panelHiddenHeight(panel)
  const x = root?.scrollLeft ?? 0
  const y = root?.scrollTop ?? 0
  return {
    width,
    height,
    scale: attempt.scale,
    type: attempt.type,
    ...(attempt.quality != null ? { quality: attempt.quality } : {}),
    style: (x || y) ? { transform: `translate(${-x}px, ${-y}px)` } : undefined,
    filter: (node) => shouldCaptureNode(node, win),
    fetch: { placeholderImage: TRANSPARENT_PLACEHOLDER },
    ...(panel ? { onCloneNode: (cloned) => { unclipPanelInClone(cloned) } } : {}),
  }
}

// One capture: PNG, then JPEG, until one fits under the cap.
// { blob, status: 'ok' | 'too_large' | 'none' }. `capture` is injectable so
// this is testable without a DOM; a thrown error propagates to the caller.
export async function captureFeedbackBlob({ root, panel = null, capture = domToBlob, win = globalThis.window } = {}) {
  if (!root) return { blob: null, status: 'none' }
  for (const attempt of CAPTURE_ATTEMPTS) {
    const blob = await capture(root, captureOptions(root, attempt, win, panel))
    if (blob && blob.size <= FEEDBACK_SCREENSHOT_MAX_BYTES) return { blob, status: 'ok' }
  }
  return { blob: null, status: 'too_large' }
}

// Starts the capture in the background and returns { promise } at once; the
// promise always resolves (never rejects) to { blob, status } within
// timeoutMs: 'timeout' when it took too long (a late result is dropped),
// 'error' when it threw.
export function startFeedbackCapture({ root, panel = null, capture = domToBlob, timeoutMs = FEEDBACK_SCREENSHOT_CAPTURE_TIMEOUT_MS, win = globalThis.window } = {}) {
  let timer = null
  const work = captureFeedbackBlob({ root, panel, capture, win }).catch((err) => {
    console.warn('[feedback] screenshot capture failed', err)
    return { blob: null, status: 'error' }
  })
  const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve({ blob: null, status: 'timeout' }), timeoutMs) })
  const promise = Promise.race([work, timeout]).finally(() => clearTimeout(timer))
  return { promise }
}

// What Submit sends: the capture's result (waiting for it, within its cap),
// or 'none' when no capture was started.
export async function finishFeedbackCapture(handle) {
  if (!handle?.promise) return { blob: null, status: 'none' }
  return handle.promise
}

// Browser-safe binary -> base64 (no Buffer). Chunked so spreading the byte
// array as call arguments never approaches an engine's argument-count limit
// on a multi-hundred-KB image.
export function arrayBufferToBase64(buffer) {
  let binary = ''
  const bytes = new Uint8Array(buffer)
  const chunkSize = 0x8000
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize))
  }
  return btoa(binary)
}

// The request body's screenshot fields for a capture result.
export async function feedbackScreenshotPayload(result) {
  const status = SCREENSHOT_STATUSES.includes(result?.status) ? result.status : 'none'
  if (status !== 'ok' || !result.blob) return { screenshotStatus: status === 'ok' ? 'none' : status }
  const buf = await result.blob.arrayBuffer()
  return { screenshot: { data: arrayBufferToBase64(buf), contentType: result.blob.type || 'image/png' }, screenshotStatus: 'ok' }
}
